import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IAgreementDetailResponse } from '../../../model/base/response/agreement-response';

interface AgreementDetailState {
  agreement: IAgreementDetailResponse | null;
  loading: boolean;
  error: string | null;
}

type AgreementDetailAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: IAgreementDetailResponse }
  | { type: 'LOAD_ERROR'; payload: string };

const initialState: AgreementDetailState = {
  agreement: null,
  loading: false,
  error: null,
};

const AgreementDetailStateContext = createContext<AgreementDetailState | undefined>(undefined);
const AgreementDetailDispatchContext = createContext<React.Dispatch<AgreementDetailAction> | undefined>(undefined);

function agreementDetailReducer(state: AgreementDetailState, action: AgreementDetailAction): AgreementDetailState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, agreement: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

export function AgreementDetailProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(agreementDetailReducer, initialState);

  return (
    <AgreementDetailStateContext.Provider value={state}>
      <AgreementDetailDispatchContext.Provider value={dispatch}>
        {children}
      </AgreementDetailDispatchContext.Provider>
    </AgreementDetailStateContext.Provider>
  );
}

export function useAgreementDetailState() {
  const context = useContext(AgreementDetailStateContext);
  if (context === undefined) {
    throw new Error('useAgreementDetailState must be used within AgreementDetailProvider');
  }
  return context;
}

export function useAgreementDetailDispatch() {
  const context = useContext(AgreementDetailDispatchContext);
  if (context === undefined) {
    throw new Error('useAgreementDetailDispatch must be used within AgreementDetailProvider');
  }
  return context;
}
